import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import TemplateLibraryTable from './components/TemplateLibraryTable';
import TemplatePreview from './components/TemplatePreview';

function DigitalFormManagement() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('companyForms');
  const [companySearch, setCompanySearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [companyForms, setCompanyForms] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const incomingToast = String(location.state?.toastMessage || '').trim();
    if (!incomingToast) return;
    setToastMessage(incomingToast);
    setActiveTab('companyForms');
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError('');
        const [templatesResponse, companyFormsResponse] = await Promise.all([
          dataAPI.getDigitalFormTemplates(),
          dataAPI.getDigitalFormClientForms(),
        ]);

        if (templatesResponse?.status === 'success') {
          const apiTemplates = Array.isArray(templatesResponse.data) ? templatesResponse.data : [];
          setTemplates(apiTemplates);
        } else {
          setTemplates([]);
        }

        if (companyFormsResponse?.status === 'success') {
          const apiCompanyForms = Array.isArray(companyFormsResponse.data)
            ? companyFormsResponse.data
            : [];
          const activeCompanyForms = apiCompanyForms.filter((item) => item?.is_active !== false);
          setCompanyForms(activeCompanyForms);
        } else {
          setCompanyForms([]);
        }

        if (templatesResponse?.status !== 'success' && companyFormsResponse?.status !== 'success') {
          setError('Failed to load digital form data.');
        }
      } catch (err) {
        setTemplates([]);
        setCompanyForms([]);
        setError(err?.message || 'Failed to load digital form data.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleCreateFromTemplate = (template) => {
    navigate(APP_URLS.DIGITAL_FORM_USE_TEMPLATE, {
      state: { template },
    });
  };

  const handleEditCompanyForm = (form) => {
    const editSource = {
      ...form,
      id: form?.template_id || form?.id,
      client_form_id: form?.id,
    };
    navigate(APP_URLS.DIGITAL_FORM_USE_TEMPLATE, {
      state: { template: editSource, isEdit: true },
    });
  };

  const handleDeleteCompanyForm = async (formId) => {
    const ok = window.confirm('Are you sure you want to delete this company form?');
    if (!ok) return;

    try {
      await dataAPI.deleteDigitalFormClientForm(formId);
      setCompanyForms((prev) => prev.filter((form) => form.id !== formId));
    } catch (err) {
      window.alert(err?.message || 'Failed to delete company form.');
    }
  };

  const filteredCompanyForms = companyForms.filter((form) =>
    String(form?.title || '').toLowerCase().includes(companySearch.trim().toLowerCase()),
  );

  const filteredTemplates = templates.filter((template) =>
    String(template?.title || '').toLowerCase().includes(templateSearch.trim().toLowerCase()),
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {previewTemplate && (
        <TemplatePreview
          mode={mode}
          template={previewTemplate}
          onBack={() => setPreviewTemplate(null)}
          onUseTemplate={handleCreateFromTemplate}
        />
      )}

      {!previewTemplate && (
        <>
          <div style={tabContainerStyle(mode)}>
            <button
              type="button"
              onClick={() => setActiveTab('companyForms')}
              style={tabBtnStyle(mode, activeTab === 'companyForms')}
            >
              Company forms
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('formTemplate')}
              style={tabBtnStyle(mode, activeTab === 'formTemplate')}
            >
              Template Library
            </button>
          </div>

          {!isLoading && error && (
            <div
              style={{
                border: `1px solid ${mode === 'dark' ? '#704040' : '#f1b2b2'}`,
                background: mode === 'dark' ? '#2b1717' : '#fff3f3',
                color: mode === 'dark' ? '#ffb0b0' : '#a53030',
                borderRadius: 8,
                padding: '12px 14px',
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {!isLoading && !error && activeTab === 'companyForms' && (
            <>
              <div style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={companySearch}
                  onChange={(event) => setCompanySearch(event.target.value)}
                  placeholder="Search company form by title..."
                  style={searchInputStyle(mode)}
                />
              </div>
              {filteredCompanyForms.length === 0 ? (
                <div
                  style={{
                    border: `1px solid ${mode === 'dark' ? '#3e3e3e' : '#e3e6e9'}`,
                    background: mode === 'dark' ? '#1a1a1a' : '#fff',
                    color: mode === 'dark' ? '#d5d5d5' : '#555',
                    borderRadius: 10,
                    padding: '16px',
                  }}
                >
                  No active company forms found.
                </div>
              ) : (
                <section
                  style={{
                    border: `1px solid ${mode === 'dark' ? '#363636' : '#e4e6e8'}`,
                    borderRadius: 12,
                    background: mode === 'dark' ? '#141414' : '#fff',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '12px 14px',
                      fontWeight: 600,
                      color: mode === 'dark' ? '#fff' : '#222',
                      borderBottom: `1px solid ${mode === 'dark' ? '#2f2f2f' : '#eceff1'}`,
                    }}
                  >
                    Company forms
                  </div>
                  <div style={{ maxHeight: 560, overflowY: 'auto', overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        minWidth: 760,
                        borderCollapse: 'collapse',
                      }}
                    >
                      <thead>
                        <tr style={{ background: mode === 'dark' ? '#1b1b1b' : '#f8fafc' }}>
                          <th style={{ ...headerCellStyle(mode), width: '30%' }}>Form Title</th>
                          <th style={{ ...headerCellStyle(mode), width: '36%' }}>Description</th>
                          <th style={{ ...headerCellStyle(mode), width: '15%', textAlign: 'center' }}>
                            Fields
                          </th>
                          <th style={{ ...headerCellStyle(mode), width: '12%' }}>Status</th>
                          <th style={{ ...headerCellStyle(mode), width: '12%', textAlign: 'center' }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompanyForms.map((form) => {
                          const schema = Array.isArray(form.schema_json) ? form.schema_json : [];
                          return (
                            <tr key={form.id}>
                              <td style={bodyCellStyle(mode, 600)}>
                                {form.title || `Form ${form.id}`}
                              </td>
                              <td style={descriptionCellStyle(mode)}>
                                <div
                                  style={descriptionClampStyle()}
                                  title={form.description || 'No description'}
                                >
                                  {form.description || 'No description'}
                                </div>
                              </td>
                              <td style={{ ...bodyCellStyle(mode), textAlign: 'center' }}>{schema.length}</td>
                              <td style={bodyCellStyle(mode)}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    borderRadius: 999,
                                    padding: '4px 10px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#067647',
                                    background: '#ecfdf3',
                                  }}
                                >
                                  Active
                                </span>
                              </td>
                              <td style={{ ...bodyCellStyle(mode), textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', gap: 8 }}>
                                  <button
                                    type="button"
                                    title="Edit form"
                                    onClick={() => handleEditCompanyForm(form)}
                                    style={iconButtonStyle(mode)}
                                  >
                                    <EditOutlinedIcon style={{ fontSize: 18 }} />
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete form"
                                    onClick={() => handleDeleteCompanyForm(form.id)}
                                    style={iconButtonStyle(mode)}
                                  >
                                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}

          {!isLoading && !error && activeTab === 'formTemplate' && (
            <>
              <div style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={templateSearch}
                  onChange={(event) => setTemplateSearch(event.target.value)}
                  placeholder="Search template by title..."
                  style={searchInputStyle(mode)}
                />
              </div>
              {filteredTemplates.length === 0 ? (
                <div
                  style={{
                    border: `1px solid ${mode === 'dark' ? '#3e3e3e' : '#e3e6e9'}`,
                    background: mode === 'dark' ? '#1a1a1a' : '#fff',
                    color: mode === 'dark' ? '#d5d5d5' : '#555',
                    borderRadius: 10,
                    padding: '16px',
                  }}
                >
                  No templates available yet.
                </div>
              ) : (
                <TemplateLibraryTable
                  mode={mode}
                  templates={filteredTemplates}
                  onPreview={setPreviewTemplate}
                  onUseTemplate={handleCreateFromTemplate}
                />
              )}
            </>
          )}
        </>
      )}

      {toastMessage ? (
        <div style={toastStyle(mode)} role="status" aria-live="polite">
          <div style={toastContentStyle()}>
            <CheckCircleIcon style={toastIconStyle()} />
            <span>{toastMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setToastMessage('')}
            style={toastCloseBtnStyle(mode)}
            title="Close"
            aria-label="Close success message"
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

const tabBtnStyle = (mode, active) => ({
  border: 'none',
  borderBottom: `2px solid ${active ? '#009391' : 'transparent'}`,
  borderRadius: 0,
  padding: '10px 4px 8px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  background: 'transparent',
  color: active ? '#009391' : mode === 'dark' ? '#d0d5dd' : '#475467',
});

const tabContainerStyle = (mode) => ({
  display: 'flex',
  gap: 24,
  marginBottom: 16,
  borderBottom: `1px solid ${mode === 'dark' ? '#2f2f2f' : '#e5e7eb'}`,
});

const searchInputStyle = (mode) => ({
  width: '100%',
  maxWidth: 360,
  border: `1px solid ${mode === 'dark' ? '#4a4a4a' : '#d0d5dd'}`,
  borderRadius: 8,
  background: mode === 'dark' ? '#191919' : '#fff',
  color: mode === 'dark' ? '#fff' : '#111',
  padding: '9px 10px',
  fontSize: 13,
});

const headerCellStyle = (mode) => ({
  textAlign: 'left',
  padding: '12px 14px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.3,
  color: mode === 'dark' ? '#d0d5dd' : '#475467',
  borderBottom: `1px solid ${mode === 'dark' ? '#2f2f2f' : '#eceff1'}`,
});

const bodyCellStyle = (mode, fontWeight = 500) => ({
  padding: '12px 14px',
  fontSize: 13,
  fontWeight,
  color: mode === 'dark' ? '#e4e7ec' : '#344054',
  borderBottom: `1px solid ${mode === 'dark' ? '#252525' : '#f0f1f3'}`,
  verticalAlign: 'middle',
});

const descriptionCellStyle = (mode) => ({
  ...bodyCellStyle(mode),
  maxWidth: 420,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'top',
});

const descriptionClampStyle = () => ({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.45,
  maxHeight: '4.35em',
});

const iconButtonStyle = (mode) => ({
  border: `1px solid ${mode === 'dark' ? '#3f3f3f' : '#d0d5dd'}`,
  borderRadius: 8,
  background: mode === 'dark' ? '#1d1d1d' : '#fff',
  color: mode === 'dark' ? '#e4e7ec' : '#344054',
  width: 34,
  height: 34,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const toastStyle = (mode) => ({
  position: 'fixed',
  top: 20,
  right: 20,
  zIndex: 2000,
  minWidth: 280,
  maxWidth: 420,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 8,
  border: `1px solid ${mode === 'dark' ? '#2e6b69' : '#9be3df'}`,
  background: mode === 'dark' ? '#122c2b' : '#ecfbfa',
  color: mode === 'dark' ? '#8de0db' : '#0c6a65',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
  fontSize: 14,
});

const toastContentStyle = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
});

const toastIconStyle = () => ({
  fontSize: 20,
  color: '#009391',
  flexShrink: 0,
});

const toastCloseBtnStyle = (mode) => ({
  border: 'none',
  background: 'transparent',
  color: mode === 'dark' ? '#8de0db' : '#0c6a65',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 2,
});

export default DigitalFormManagement;
